import { Loader } from "@googlemaps/js-api-loader";
import debounce from "lodash.debounce";
import React, { useEffect, useRef, useState, useCallback } from "react";
import { useSelector } from "react-redux";

interface GoogleMapWithPolygonProps {
  getMapCoordinates?: (coords: any, center: [number, number]) => void;
}

const GoogleMapWithPolygon: React.FC<GoogleMapWithPolygonProps> = ({ getMapCoordinates }) => {
  const API_KEY = "AIzaSyAqQuX2O1PHI9uihjJnOrYHwkkyxdNcfNo";

  const [keyword, setKeyword] = useState("");
  const [suggestions, setSuggestions] = useState<Array<{ description: string; place_id: string }>>([]);
  // Business/Form state: Configured office position
  const [officePosition, setOfficePosition] = useState<[number, number] | null>(null);
  // Navigation/Transient state: User GPS location (never sent to form or API)
  const [userLocation, setUserLocation] = useState<[number, number] | null>(null);
  const [cord, setCord] = useState<any[]>([]);
  const [isMapLoaded, setIsMapLoaded] = useState(false);
  const [isLocating, setIsLocating] = useState(false);

  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<any>(null);
  const officeMarkerRef = useRef<any>(null);
  const userLocationMarkerRef = useRef<any>(null);
  const searchLocationMarkerRef = useRef<any>(null);
  const polygonRef = useRef<any>(null);
  const drawingManagerRef = useRef<any>(null);
  const autocompleteServiceRef = useRef<any>(null);
  const placesServiceRef = useRef<any>(null);
  const isInitialPolygonCenteredRef = useRef<boolean>(false);

  const officeLocationState = useSelector((s: any) => s.officeLocationState);

  // Helper function to update polygon coordinates and calculate office center
  const updatePolygonCoordinates = useCallback((polygon: any) => {
    if (!polygon || !(window as any).google) return;

    const google = (window as any).google;
    const path = polygon.getPath();
    const coordinates = path.getArray().map((latLng: any) => ({
      lat: latLng.lat(),
      lng: latLng.lng()
    }));

    // Calculate center using bounds
    const bounds = new google.maps.LatLngBounds();
    coordinates.forEach((coord: any) => bounds.extend(new google.maps.LatLng(coord.lat, coord.lng)));
    const center = bounds.getCenter();
    const newOfficePos: [number, number] = [center.lat(), center.lng()];

    setCord([coordinates]);
    setOfficePosition(newOfficePos);

    if (getMapCoordinates) {
      getMapCoordinates([coordinates], newOfficePos);
    }
  }, [getMapCoordinates]);

  // Create or update Office Location marker (draggable, modifies officePosition)
  const createOfficeMarker = useCallback((map: any, pos: [number, number]) => {
    if (!map || !(window as any).google) return;

    const google = (window as any).google;

    // Remove existing office marker
    if (officeMarkerRef.current) {
      officeMarkerRef.current.setMap(null);
    }

    const marker = new google.maps.Marker({
      position: { lat: pos[0], lng: pos[1] },
      map: map,
      draggable: true,
      title: "Office Location"
    });

    officeMarkerRef.current = marker;

    // Dragging the office marker updates the office position and notifies the form
    google.maps.event.addListener(marker, "dragend", (event: any) => {
      if (event.latLng) {
        const newPos: [number, number] = [event.latLng.lat(), event.latLng.lng()];
        setOfficePosition(newPos);
        if (getMapCoordinates) {
          getMapCoordinates(cord, newPos);
        }
      }
    });
  }, [cord, getMapCoordinates]);

  // Create or update Search Location indicator marker
  const createSearchLocationMarker = useCallback((map: any, location: any, title?: string) => {
    if (!map || !(window as any).google) return;

    const google = (window as any).google;

    if (searchLocationMarkerRef.current) {
      searchLocationMarkerRef.current.setMap(null);
    }

    const searchMarker = new google.maps.Marker({
      position: location,
      map: map,
      draggable: false,
      title: title || "Searched Location",
      animation: google.maps.Animation.DROP,
    });

    // Info window with search label
    const infoWindow = new google.maps.InfoWindow({
      content: `<div style="font-weight: 500; font-size: 13px; padding: 2px 4px;">${title || "Searched Location"}</div>`
    });

    searchMarker.addListener("click", () => {
      infoWindow.open(map, searchMarker);
    });

    searchLocationMarkerRef.current = searchMarker;
  }, []);

  // Create or update User Location marker (distinct, non-draggable, transient GPS indicator)
  const createUserLocationMarker = useCallback((map: any, pos: [number, number]) => {
    if (!map || !(window as any).google) return;

    const google = (window as any).google;

    if (userLocationMarkerRef.current) {
      userLocationMarkerRef.current.setMap(null);
    }

    const userMarker = new google.maps.Marker({
      position: { lat: pos[0], lng: pos[1] },
      map: map,
      draggable: false,
      title: "Your Current Location",
      icon: {
        path: google.maps.SymbolPath.CIRCLE,
        scale: 8,
        fillColor: "#4285F4",
        fillOpacity: 1,
        strokeColor: "#ffffff",
        strokeWeight: 2,
      },
    });

    userLocationMarkerRef.current = userMarker;
  }, []);

  // Initialize Google Maps
  useEffect(() => {
    const initMap = async () => {
      try {
        const loader = new Loader({
          apiKey: API_KEY,
          version: "3.64",
          libraries: ["places", "drawing", "geometry"]
        });

        await loader.load();

        if (!mapRef.current || !(window as any).google) return;

        const google = (window as any).google;

        // Initialize map
        const map = new google.maps.Map(mapRef.current, {
          center: officePosition ? { lat: officePosition[0], lng: officePosition[1] } : { lat: 28.6139, lng: 77.2090 }, // Delhi coordinates
          zoom: 15,
          maxZoom: 22,
          mapTypeId: google.maps.MapTypeId.ROADMAP,
        });

        mapInstanceRef.current = map;

        // Initialize services
        autocompleteServiceRef.current = new google.maps.places.AutocompleteService();
        placesServiceRef.current = new google.maps.places.PlacesService(map);

        // Initialize drawing manager
        const drawingManager = new google.maps.drawing.DrawingManager({
          drawingMode: null,
          drawingControl: true,
          drawingControlOptions: {
            position: google.maps.ControlPosition.TOP_CENTER,
            drawingModes: [google.maps.drawing.OverlayType.POLYGON],
          },
          polygonOptions: {
            fillColor: "#FF0000",
            fillOpacity: 0.35,
            strokeWeight: 2,
            strokeColor: "#FF0000",
            clickable: false,
            editable: true,
            zIndex: 1,
          },
        });

        drawingManager.setMap(map);
        drawingManagerRef.current = drawingManager;

        // Add polygon complete listener
        google.maps.event.addListener(drawingManager, "polygoncomplete", (polygon: any) => {
          console.log("Polygon created");

          // Remove previous polygon
          if (polygonRef.current) {
            polygonRef.current.setMap(null);
          }

          polygonRef.current = polygon;

          // Update coordinates immediately
          updatePolygonCoordinates(polygon);

          // Set drawing mode to null after drawing
          drawingManager.setDrawingMode(null);

          // Add listeners for polygon editing
          const path = polygon.getPath();

          google.maps.event.addListener(path, "set_at", () => {
            console.log("Polygon vertex moved");
            updatePolygonCoordinates(polygon);
          });

          google.maps.event.addListener(path, "insert_at", () => {
            console.log("Polygon vertex added");
            updatePolygonCoordinates(polygon);
          });

          google.maps.event.addListener(path, "remove_at", () => {
            console.log("Polygon vertex removed");
            updatePolygonCoordinates(polygon);
          });
        });

        setIsMapLoaded(true);

        // Initialize office marker if officePosition exists
        if (officePosition) {
          createOfficeMarker(map, officePosition);
        }

        console.log("Google Maps initialized successfully");

      } catch (error) {
        console.error("Error loading Google Maps:", error);
      }
    };

    initMap();

    return () => {
      // Cleanup
      if (officeMarkerRef.current) {
        officeMarkerRef.current.setMap(null);
      }
      if (userLocationMarkerRef.current) {
        userLocationMarkerRef.current.setMap(null);
      }
      if (searchLocationMarkerRef.current) {
        searchLocationMarkerRef.current.setMap(null);
      }
      if (polygonRef.current) {
        polygonRef.current.setMap(null);
      }
      if (drawingManagerRef.current) {
        drawingManagerRef.current.setMap(null);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Update map and office marker when configured officePosition changes
  useEffect(() => {
    if (officePosition && mapInstanceRef.current && isMapLoaded) {
      const map = mapInstanceRef.current;
      createOfficeMarker(map, officePosition);
    }
  }, [officePosition, createOfficeMarker, isMapLoaded]);

  // Load existing coordinates and position on fetch
  useEffect(() => {
    if (
      officeLocationState.loading === "fulfilled" &&
      officeLocationState.action === "find" &&
      officeLocationState.data
    ) {
      const { lat, long, coordinates } = officeLocationState.data || {};
      if (lat != null && long != null && !isNaN(+lat) && !isNaN(+long)) {
        setOfficePosition([+lat, +long]);
        setCord(coordinates || []);
      }
    } else if (
      officeLocationState.loading !== "pending" &&
      !officePosition
    ) {
      // For create mode / initial fallback, initialize officePosition to Delhi
      setOfficePosition([28.6139, 77.2090]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [officeLocationState.loading, officeLocationState.action, officeLocationState.data]);

  // Explicit user-triggered action: Use Current Location (Camera navigation ONLY - does NOT touch office form coordinates)
  const handleUseCurrentLocation = () => {
    if (!navigator.geolocation) {
      alert("Geolocation is not supported by your browser.");
      return;
    }

    setIsLocating(true);

    const onLocationSuccess = (p: GeolocationPosition) => {
      setIsLocating(false);
      const lat = p.coords.latitude;
      const lng = p.coords.longitude;
      const newGpsPos: [number, number] = [lat, lng];
      setUserLocation(newGpsPos);

      if (mapInstanceRef.current && (window as any).google) {
        const google = (window as any).google;
        const targetLatLng = new google.maps.LatLng(lat, lng);
        mapInstanceRef.current.setCenter(targetLatLng);
        mapInstanceRef.current.panTo(targetLatLng);
        mapInstanceRef.current.setZoom(17);
        createUserLocationMarker(mapInstanceRef.current, newGpsPos);
      }

      // NOTE: Intentionally DO NOT call getMapCoordinates() here!
      // User GPS is for camera navigation only and must NOT overwrite office location data.
    };

    // First attempt: Request with low accuracy or cached position (fast and reliable on desktops/Wi-Fi)
    navigator.geolocation.getCurrentPosition(
      onLocationSuccess,
      () => {
        // Fallback: Attempt with highAccuracy
        navigator.geolocation.getCurrentPosition(
          onLocationSuccess,
          (err) => {
            setIsLocating(false);
            let msg = "Unable to retrieve your location: " + err.message;
            if (err.code === err.PERMISSION_DENIED) {
              msg = "Location permission was denied. Please allow location access in your browser.";
            }
            alert(msg);
          },
          { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 }
        );
      },
      { enableHighAccuracy: false, timeout: 8000, maximumAge: 60000 }
    );
  };

  // Load existing polygon when coordinates are available
  useEffect(() => {
    if (cord.length > 0 && mapInstanceRef.current && cord[0]?.length > 0 && isMapLoaded && (window as any).google) {
      console.log("Loading existing polygon", cord);

      const google = (window as any).google;

      // Remove existing polygon
      if (polygonRef.current) {
        polygonRef.current.setMap(null);
      }

      // Create new polygon from coordinates
      const coordinates = cord[0].map((c: any) => ({
        lat: typeof c.lat === 'number' ? c.lat : parseFloat(c.lat),
        lng: typeof c.lng === 'number' ? c.lng : (typeof c.long === 'number' ? c.long : parseFloat(c.long || c.lng))
      }));

      console.log("Processed coordinates:", coordinates);

      if (coordinates.length > 0) {
        const polygon = new google.maps.Polygon({
          paths: coordinates,
          fillColor: "#FF0000",
          fillOpacity: 0.35,
          strokeWeight: 2,
          strokeColor: "#FF0000",
          editable: true,
          draggable: false,
        });

        polygon.setMap(mapInstanceRef.current);
        polygonRef.current = polygon;

        // Add listeners for editing
        const path = polygon.getPath();
        google.maps.event.addListener(path, "set_at", () => {
          console.log("Existing polygon vertex moved");
          updatePolygonCoordinates(polygon);
        });

        google.maps.event.addListener(path, "insert_at", () => {
          console.log("Existing polygon vertex added");
          updatePolygonCoordinates(polygon);
        });

        google.maps.event.addListener(path, "remove_at", () => {
          console.log("Existing polygon vertex removed");
          updatePolygonCoordinates(polygon);
        });

        // Center map on polygon only on initial load if not already centered
        if (!isInitialPolygonCenteredRef.current) {
          const bounds = new google.maps.LatLngBounds();
          coordinates.forEach((coord: any) => bounds.extend(new google.maps.LatLng(coord.lat, coord.lng)));
          mapInstanceRef.current.fitBounds(bounds);
          isInitialPolygonCenteredRef.current = true;
        }

        console.log("Existing polygon loaded successfully");
      }
    }
  }, [cord, updatePolygonCoordinates, isMapLoaded]);

  // Fetch autocomplete suggestions
  const fetchSuggestions = async (text: string) => {
    if (!text || !autocompleteServiceRef.current) {
      setSuggestions([]);
      return;
    }

    try {
      const request = {
        input: text,
        language: "en",
        componentRestrictions: { country: "in" } // Restrict to India for better results
      };

      const google = (window as any).google;
      autocompleteServiceRef.current.getPlacePredictions(request, (predictions: any, status: any) => {
        if (status === google.maps.places.PlacesServiceStatus.OK && predictions) {
          setSuggestions(
            predictions.slice(0, 5).map((prediction: any) => ({
              description: prediction.description,
              place_id: prediction.place_id,
            }))
          );
        } else {
          setSuggestions([]);
        }
      });
    } catch (error) {
      console.error("Error fetching suggestions:", error);
      setSuggestions([]);
    }
  };

  const debouncedFetch = useRef(debounce(fetchSuggestions, 300)).current;

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setKeyword(e.target.value);
    debouncedFetch(e.target.value);
  };

  // Place selection: Map navigation ONLY (does NOT alter office coordinates)
  const handleSelect = async (p: { place_id: string; description: string }) => {
    setKeyword(p.description);
    setSuggestions([]);

    if (!placesServiceRef.current) return;

    const request = {
      placeId: p.place_id,
      fields: ['geometry', 'name']
    };

    const google = (window as any).google;
    placesServiceRef.current.getDetails(request, (place: any, status: any) => {
      if (status === google.maps.places.PlacesServiceStatus.OK && place?.geometry?.location) {
        const location = place.geometry.location;
        if (mapInstanceRef.current) {
          mapInstanceRef.current.panTo(location);
          mapInstanceRef.current.setZoom(16);
          // Show visible indicator marker on the exact searched location
          createSearchLocationMarker(mapInstanceRef.current, location, p.description);
        }
        // NOTE: Intentionally DO NOT modify officePosition or call getMapCoordinates() here.
        // Search is for map navigation only.
      }
    });
  };

  // Navigate/Center map to office polygon boundary
  const handleFitToPolygon = () => {
    if (!mapInstanceRef.current || !(window as any).google) return;
    const google = (window as any).google;

    if (polygonRef.current) {
      const bounds = new google.maps.LatLngBounds();
      polygonRef.current.getPath().forEach((latLng: any) => bounds.extend(latLng));
      mapInstanceRef.current.fitBounds(bounds);
    } else if (cord.length > 0 && cord[0]?.length > 0) {
      const bounds = new google.maps.LatLngBounds();
      cord[0].forEach((c: any) => {
        const lat = typeof c.lat === 'number' ? c.lat : parseFloat(c.lat);
        const lng = typeof c.lng === 'number' ? c.lng : (typeof c.long === 'number' ? c.long : parseFloat(c.long || c.lng));
        bounds.extend(new google.maps.LatLng(lat, lng));
      });
      mapInstanceRef.current.fitBounds(bounds);
    } else if (officePosition) {
      mapInstanceRef.current.panTo({ lat: officePosition[0], lng: officePosition[1] });
      mapInstanceRef.current.setZoom(16);
    } else {
      alert("No boundary polygon has been drawn yet.");
    }
  };

  // Clear polygon function
  const clearPolygon = () => {
    if (polygonRef.current) {
      polygonRef.current.setMap(null);
      polygonRef.current = null;
    }
    setCord([]);
    if (getMapCoordinates) {
      getMapCoordinates([], officePosition || [0, 0]);
    }
  };

  return (
    <>
      {/* Search Input */}
      <div className="mb-3 position-relative" style={{ maxWidth: 400 }}>
        <input
          type="text"
          className="form-control"
          placeholder="Search for a location"
          value={keyword}
          onChange={handleChange}
        />
        {suggestions.length > 0 && (
          <ul
            className="list-group position-absolute w-100"
            style={{ zIndex: 2000, maxHeight: 240, overflowY: "auto" }}
          >
            {suggestions.map((s) => (
              <li
                key={s.place_id}
                className="list-group-item list-group-item-action"
                onClick={() => handleSelect(s)}
                style={{ cursor: "pointer" }}
              >
                {s.description}
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Map Controls */}
      <div className="mb-3">
        <div className="mb-2">
          <label
            className="fw-semibold text-dark mb-1"
            style={{ cursor: (polygonRef.current || (cord.length > 0 && cord[0]?.length > 0)) ? "pointer" : "default" }}
            onClick={handleFitToPolygon}
            title="Click to view configured office boundary on map"
          >
            Map Boundary <span className="text-danger">*</span>
          </label>
          <span className="text-muted fs-12 ms-2">
            (Draw office polygon boundaries on the map)
          </span>
        </div>
        <div className="d-flex align-items-center flex-wrap gap-2 mb-2">
          <button
            type="button"
            className="btn btn-outline-primary btn-sm"
            onClick={handleUseCurrentLocation}
            disabled={isLocating}
            title="Pan map to your current GPS position"
          >
            <i className={`ti ${isLocating ? "ti-loader" : "ti-map-pin"} me-1`} />
            {isLocating ? "Getting Location..." : "Use Current Location"}
          </button>
          <button
            type="button"
            className="btn btn-outline-success btn-sm"
            onClick={handleFitToPolygon}
            disabled={!polygonRef.current && !(cord.length > 0 && cord[0]?.length > 0)}
            title="Center and zoom map to the office geo-fenced boundary"
          >
            <i className="ti ti-polygon me-1" />
            Go to Office Boundary
          </button>
          <button
            type="button"
            className="btn btn-outline-danger btn-sm"
            onClick={clearPolygon}
            disabled={!polygonRef.current}
          >
            Clear Polygon
          </button>
        </div>
        <small className="text-muted">
          Use the polygon tool in the map to draw office boundaries. Click and drag vertices to edit.
        </small>
      </div>

      {/* Google Map Container */}
      <div
        ref={mapRef}
        style={{ height: 500, width: "100%" }}
        className="border rounded"
      />
    </>
  );
};

export default GoogleMapWithPolygon;